import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, ListChecks, Loader2, Plus, RotateCcw, Trash2 } from "lucide-react";
import { API, formatApiError } from "../auth/api";
import { danlonBase, dataloenBase, termsBase } from "../lib/payCodes";

/**
 * 2026-10-25: Lønsystemernes lister - Danløns løntyper, DataLøns løndele og
 * lønterminer. De er de samme for alle kunder, så de vedligeholdes her (ikke af
 * hver kunde). De indbyggede står i appen; her tilføjes nye, navne rettes, og
 * typer, lønsystemet ikke har længere, skjules - uden en ny version af appen.
 * Kunderne vælger stadig selv deres koder og løntermin (Løn › Regler).
 */
const input = "px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40";
const btn = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold";
const COLS = [["E", "Antal"], ["B", "Beløb"], ["EB", "Antal og beløb"]];
const CATS = [["", "Løn"], ["count", "Kun antal"], ["deduction", "Fradrag"], ["benefit", "Personalegode"], ["taxfree", "Skattefri"],
  ["sick", "Sygeløn"], ["noproc", "Uden lønbehandling"]];

// 2026-10-25: Rækker i stedet for en tabel - på en telefon lå "Slet" uden for skærmen.
function ListSection({ title, help, rows, columns, onChange, onReset, onRemove, onToggle, addForm, filter, setFilter, testid, usage, effect }) {
  const shown = rows.filter((r) => !filter || `${r.code} ${r.name || r.label}`.toLowerCase().includes(filter.toLowerCase()));
  // Før en kode slettes eller skjules: hvem bruger den, og hvad sker der så (gemte lønperioder røres aldrig).
  const confirmIt = (r, verb) => {
    const who = usage?.[r.code] || [];
    if (!who.length) return true;
    return window.confirm(`${r.code} bruges af ${who.join(", ")}.\n\n${effect}\n\nGodkendte lønperioder og gemte timer ændres ikke.\n\n${verb} alligevel?`);
  };
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 space-y-3" data-testid={testid}>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[220px]">
          <div className="font-bold">{title}</div>
          <div className="text-xs text-slate-500">{help}</div>
        </div>
        {setFilter && <input className={`${input} w-48`} placeholder="Søg nummer eller navn" value={filter} onChange={(e) => setFilter(e.target.value)} />}
      </div>
      <div className="max-h-[480px] overflow-y-auto divide-y divide-slate-100 dark:divide-slate-700">
        {shown.map((r) => {
          const who = usage?.[r.code] || [];
          return (
            <div key={r.code} className={`py-1.5 flex flex-wrap items-center gap-2 ${r.hidden ? "opacity-50" : ""}`} data-testid={`${testid}-row-${r.code}`}>
              <span className="font-mono text-sm w-14 shrink-0">{r.code}</span>
              {columns.map(([k, l, opts]) => (opts ? (
                <select key={k} className={input} aria-label={l} value={r[k] ?? ""} onChange={(e) => onChange(r, { [k]: e.target.value })}>
                  {opts.map(([v, lab]) => <option key={v} value={v}>{lab}</option>)}
                </select>
              ) : <input key={k} className={`${input} flex-1 min-w-[160px]`} aria-label={l} value={r[k] ?? ""} onChange={(e) => onChange(r, { [k]: e.target.value })} />))}
              <span className="text-xs text-slate-500 whitespace-nowrap">{r.hidden ? "Skjult" : !r.builtin ? "Tilføjet" : r.changed ? "Rettet" : "Indbygget"}
                {who.length > 0 && <span className="ml-1 text-emerald-700 dark:text-emerald-400" title={who.join(", ")}>· bruges af {who.length}</span>}</span>
              <span className="ml-auto flex items-center gap-3 text-xs">
                <button type="button" className="underline" onClick={() => (r.hidden || confirmIt(r, "Skjul")) && onToggle(r)}>{r.hidden ? "Vis" : "Skjul"}</button>
                {r.builtin && r.changed && <button type="button" className="inline-flex items-center gap-1 text-slate-500" onClick={() => onReset(r)}><RotateCcw className="w-3.5 h-3.5" />Nulstil</button>}
                {!r.builtin && <button type="button" className="inline-flex items-center gap-1 text-red-700" aria-label={`Slet ${r.code}`}
                  onClick={() => confirmIt(r, "Slet") && onRemove(r)}><Trash2 className="w-3.5 h-3.5" />Slet</button>}
              </span>
            </div>
          );
        })}
      </div>
      {addForm}
    </div>
  );
}

export default function SuperAdminPaySystems() {
  const [ov, setOv] = useState(null);          // super admins rækker: { danlon, dataloen, terms }
  const [meta, setMeta] = useState({});
  const [usage, setUsage] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [f1, setF1] = useState("");
  const [f2, setF2] = useState("");
  const [nd, setNd] = useState({ code: "", name: "", cols: "EB" });
  const [nl, setNl] = useState({ code: "", name: "", cat: "" });
  const [nt, setNt] = useState({ code: "", label: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/superadmin/pay-systems`);
      setOv({ danlon: data.danlon || [], dataloen: data.dataloen || [], terms: data.terms || [] });
      setMeta({ at: data.updated_at, by: data.updated_by });
      setUsage(data.usage || {});
      setDirty(false);
    } catch (e) { toast.error(formatApiError(e)); setOv({ danlon: [], dataloen: [], terms: [] }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // De samlede lister: indbygget + super admins rækker (rettet / tilføjet / skjult).
  const merged = useMemo(() => {
    if (!ov) return null;
    const mk = (base, extra, fields) => {
      const out = (Array.isArray(base) ? base : Object.entries(base)).map(([code, v]) => ({ code, ...v, builtin: true }));
      extra.forEach((e) => {
        const i = out.findIndex((r) => r.code === e.code);
        if (i >= 0) out[i] = { ...out[i], ...Object.fromEntries(fields.map((k) => [k, e[k] || out[i][k]])), hidden: !!e.hidden, changed: true };
        else out.push({ ...e, builtin: false });
      });
      return out;
    };
    const dBase = Object.fromEntries(Object.entries(danlonBase()).map(([n, v]) => [String(n), { name: v.name, cols: v.cols }]));
    const sortNum = (a, b) => (parseInt(a.code.replace(/\D/g, ""), 10) || 0) - (parseInt(b.code.replace(/\D/g, ""), 10) || 0) || a.code.localeCompare(b.code);
    return {
      danlon: mk(dBase, ov.danlon, ["name", "cols"]).sort(sortNum),
      dataloen: mk(dataloenBase(), ov.dataloen, ["name", "cat"]).sort((a, b) => a.code.localeCompare(b.code)),
      terms: mk(termsBase().map((t) => [t.code, { label: t.label }]), ov.terms, ["label"]),   // listens rækkefølge (51 først)
    };
  }, [ov]);

  const upsert = (list, r, patch, fields) => {
    setOv((o) => {
      const cur = o[list].find((e) => e.code === r.code);
      const next = { code: r.code, ...Object.fromEntries(fields.map((k) => [k, r[k] ?? ""])), hidden: !!r.hidden, ...(cur || {}), ...patch };
      return { ...o, [list]: [...o[list].filter((e) => e.code !== r.code), next] };
    });
    setDirty(true);
  };
  const drop = (list, r) => { setOv((o) => ({ ...o, [list]: o[list].filter((e) => e.code !== r.code) })); setDirty(true); };
  const add = (list, row, ok) => {
    if (!ok) return;
    if (merged[list].some((r) => r.code === row.code)) { toast.error(`${row.code} findes allerede - ret den i listen`); return; }
    setOv((o) => ({ ...o, [list]: [...o[list], { ...row, hidden: false }] }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/superadmin/pay-systems`, ov);
      toast.success("Listerne er gemt - kunderne ser dem næste gang, de åbner Løn › Regler");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!merged) return <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  return (
    <section className="space-y-4" data-testid="pay-systems-panel">
      <div className="bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-950/30 dark:to-blue-950/30 rounded-xl p-5 border border-emerald-200 dark:border-emerald-800">
        <h2 className="font-heading font-bold text-xl flex items-center gap-2 mb-2"><ListChecks className="w-6 h-6 text-emerald-600" /> Lønsystemer</h2>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Danløns løntyper, DataLøns løndele og lønterminer er de samme for alle kunder - derfor vedligeholdes de her, ikke af hver kunde.
          Kunderne vælger selv deres koder og løntermin under Løn › Regler › Lønartkoder og kontrol, og får en advarsel ved et nummer, der ikke står her.
          Kommer der en ny løntype, så tilføj den; findes en ikke længere, så skjul den (Danløn-filen stopper ved en skjult type).
        </p>
        {meta.at && <p className="text-xs text-slate-500 mt-2">Sidst gemt {new Date(meta.at).toLocaleString("da-DK")}{meta.by ? ` af ${meta.by}` : ""}.</p>}
      </div>
      <ListSection title="Danløn - løntyper" testid="pay-systems-danlon"
        help={'Fra Danløns "Indlæs fra fil". Antal = timer, dage eller stk; beløb = kroner. T6+ og O6+ (brugertilføjede) behøver ikke stå her.'}
        rows={merged.danlon} columns={[["name", "Navn"], ["cols", "Tager", COLS]]} filter={f1} setFilter={setF1} usage={usage.danlon}
        effect="Firmaerne beholder koden, men deres Danløn-fil stopper med en besked, til de har valgt en anden løntype (Danløn afviser filer med ukendte typer)."
        onChange={(r, p) => upsert("danlon", r, p, ["name", "cols"])} onToggle={(r) => upsert("danlon", r, { hidden: !r.hidden }, ["name", "cols"])}
        onReset={(r) => drop("danlon", r)} onRemove={(r) => drop("danlon", r)}
        addForm={(
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <input className={`${input} w-20`} placeholder="Nr." value={nd.code} onChange={(e) => setNd({ ...nd, code: e.target.value.toUpperCase() })} />
            <input className={`${input} w-56`} placeholder="Navn" value={nd.name} onChange={(e) => setNd({ ...nd, name: e.target.value })} />
            <select className={input} value={nd.cols} onChange={(e) => setNd({ ...nd, cols: e.target.value })}>{COLS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            <button type="button" className={`${btn} border border-slate-200 dark:border-slate-600`} data-testid="pay-systems-add-danlon"
              onClick={() => { add("danlon", { ...nd, code: nd.code.replace(/^0+(?=\d)/, "") }, /^[TO]?\d{1,3}$/.test(nd.code) && nd.name.trim()); setNd({ code: "", name: "", cols: "EB" }); }}>
              <Plus className="w-4 h-4" />Tilføj</button>
          </div>
        )} />
      <ListSection title="DataLøn - løndele" testid="pay-systems-dataloen"
        help="Fra DataLøns løndeloversigt. Et par (fx 12x13) er antal x sats; et enkelt felt (fx 47) er et antal eller et beløb. Typen styrer advarslen ved kunden (fx et fradrag på en lønart med løn)."
        rows={merged.dataloen} columns={[["name", "Navn"], ["cat", "Type", CATS]]} filter={f2} setFilter={setF2} usage={usage.dataloen}
        effect="Firmaerne beholder koden og kan stadig lave lønfilen - de får en advarsel om, at løndelen ikke står i listen."
        onChange={(r, p) => upsert("dataloen", r, p, ["name", "cat"])} onToggle={(r) => upsert("dataloen", r, { hidden: !r.hidden }, ["name", "cat"])}
        onReset={(r) => drop("dataloen", r)} onRemove={(r) => drop("dataloen", r)}
        addForm={(
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <input className={`${input} w-24`} placeholder="47 / 12x13" value={nl.code} onChange={(e) => setNl({ ...nl, code: e.target.value.toUpperCase().replace("X", "x") })} />
            <input className={`${input} w-56`} placeholder="Navn" value={nl.name} onChange={(e) => setNl({ ...nl, name: e.target.value })} />
            <select className={input} value={nl.cat} onChange={(e) => setNl({ ...nl, cat: e.target.value })}>{CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            <button type="button" className={`${btn} border border-slate-200 dark:border-slate-600`} data-testid="pay-systems-add-dataloen"
              onClick={() => { add("dataloen", nl, /^[0-9A-Z]{2}(x[0-9A-Z]{2})?$/.test(nl.code) && nl.name.trim()); setNl({ code: "", name: "", cat: "" }); }}>
              <Plus className="w-4 h-4" />Tilføj</button>
          </div>
        )} />
      <ListSection title="DataLøn - lønterminer" testid="pay-systems-terms"
        help="Numrene, kunderne kan vælge som løntermin. Står kundens nummer ikke her, kan de skrive det selv (med en advarsel)."
        rows={merged.terms} columns={[["label", "Betydning"]]} usage={usage.terms}
        effect="Firmaerne beholder deres løntermin og kan stadig lave lønfilen - valget viser bare, at nummeret ikke står i listen."
        onChange={(r, p) => upsert("terms", r, p, ["label"])} onToggle={(r) => upsert("terms", r, { hidden: !r.hidden }, ["label"])}
        onReset={(r) => drop("terms", r)} onRemove={(r) => drop("terms", r)}
        addForm={(
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <input className={`${input} w-20`} placeholder="Nr." inputMode="numeric" value={nt.code} onChange={(e) => setNt({ ...nt, code: e.target.value })} />
            <input className={`${input} w-56`} placeholder="Betydning" value={nt.label} onChange={(e) => setNt({ ...nt, label: e.target.value })} />
            <button type="button" className={`${btn} border border-slate-200 dark:border-slate-600`}
              onClick={() => { add("terms", nt, /^\d{2}$/.test(nt.code) && nt.label.trim()); setNt({ code: "", label: "" }); }}><Plus className="w-4 h-4" />Tilføj</button>
          </div>
        )} />
      <div className="sticky bottom-3 flex items-center justify-end gap-3">
        {dirty && <span className="text-sm font-semibold text-amber-700 dark:text-amber-300 bg-white/90 dark:bg-slate-900/90 rounded-lg px-2 py-1" data-testid="pay-systems-dirty">Ændringerne er ikke gemt</span>}
        <button type="button" disabled={!dirty || saving} onClick={save} data-testid="pay-systems-save"
          className={`${btn} bg-emerald-600 text-white shadow-lg disabled:opacity-40`}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Gem lister</button>
      </div>
    </section>
  );
}
